import { useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// MUI
import Grid from '@mui/material/Grid';
import { ClearIcon } from '@mui/x-date-pickers';
import CancelIcon from '@mui/icons-material/Cancel';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { Box, TextField, Button, FormHelperText, MenuItem, Select, Stack, Tooltip, Autocomplete } from '@mui/material';

// Hooks
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { useAppSelector, useAppDispatch } from '../../../redux/hook';

// Services
import AuthService from '../../../services/auth.service';
import { getUsers } from '../../../services/autoComplete.service';
import { fetchReaderByImei, updateIsEditLockedLocation } from '../../../redux/slices/readerSlice';
import { updateTargetLocationState } from '../../../redux/slices/mapSlice';
import {
  appendLabel,
  deleteLabel,
  updateLockedLocation,
  updateReader,
  updateReaderContracts,
  updateReportInterval,
} from '../../../services/reader.service';

// Types
import { Reader } from '../../../types/Reader';
import { ResourceType } from '../../../types/ResourceType';
import { AppPermissions } from '../../../constants/Permissions';

// Components
import { AuthGuard } from '../../common/AuthGuard';
import LabelTag from '../labels/LabelTag';

// Styles
import './ReaderConfiguration.scss';

// Dropdown presets for reader report interval field
const ALLOWED_INTERVAL_SECONDS = [5, 15, 30, 60, 120, 180].map((v) => v * 60);

interface ReaderStageProp {
  reader: Reader;
  onUpdated?: (emei: string) => void;
}

interface AutoCompleteUser {
  id: string;
  label: string;
}

interface FormParams {
  opsStatus: number | null;
  suffix: string | null;
  supplyChainStatus: number | null;
  intendedUse: number | null;
  reportIntervalSeconds: number | null;
  latitude?: string | null;
  longitude?: string | null;
  labelIds?: string[] | null;
  responsibleUserId?: string | null;
  responsibleUserFullName?: string | null;
}

const configurationValidationSchema = (_isModifyLockedLocation: boolean) =>
  Yup.object().shape({
    opsStatus: Yup.number().required('This field is required'),
    supplyChainStatus: Yup.number().required('This field is required'),
    intendedUse: Yup.number().required('This field is required'),
    latitude: Yup.string()
      .test('valid-latitude', 'Latitude must be a valid number', (value) => !value || !isNaN(Number(value)))
      .nullable(),
    longitude: Yup.string()
      .test('valid-longitude', 'Longitude must be a valid number', (value) => !value || !isNaN(Number(value)))
      .nullable(),
  });

// Small helper so every row is written once, not 8 times
const FormRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <Grid item xs={12} sm={4} lg={5} className='reader-config__row'>
      <div className='reader-config__label'>{label}</div>
    </Grid>
    <Grid item xs={12} sm={8} lg={7} className='reader-config__row'>
      <div className='reader-config__control'>{children}</div>
    </Grid>
  </>
);

const ReaderConfiguration = (props: ReaderStageProp) => {
  const [isEditLockedLocation, setIsEditLockedLocation] = useState(false);
  const [users, setUsers] = useState<readonly AutoCompleteUser[]>([]);
  const [isManageContractsDialogOpen, setIsManageContractsDialogOpen] = useState(false);
  const [contractIds, setContractIds] = useState<string[]>([]);

  const { notifyMessage } = useNotifyMessage();
  const mapState = useAppSelector((state) => state.maps);
  const settingState = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();
  const canEdit = AuthService.hasPermission(AppPermissions.Reader.Edit);
  const canEditReaderReportInterval = AuthService.hasPermission(AppPermissions.Reader.ReportInterval.Edit);
  const canEditReaderIntendedUse = AuthService.hasPermission(AppPermissions.Reader.IntendedUse.Edit);

  useEffect(() => {
    let newLat: number | undefined = undefined;
    let newLon: number | undefined = undefined;

    if (mapState.targetLocation && isEditLockedLocation) {
      newLat = mapState.targetLocation.latitude;
      newLon = mapState.targetLocation.longitude;
    } else if (!isEditLockedLocation && props.reader) {
      newLat = props.reader?.lockedLocation?.latitude;
      newLon = props.reader?.lockedLocation?.longitude;
    }

    if (newLat !== undefined && newLat !== null) formik.setFieldValue('latitude', newLat.toString());
    if (newLon !== undefined && newLon !== null) formik.setFieldValue('longitude', newLon.toString());
  }, [mapState.targetLocation, isEditLockedLocation, props.reader]);

  useEffect(() => {
    setContractIds(props.reader?.contractIds ?? []);
  }, [props.reader]);

  const handleSubmitForm = async (values: FormParams) => {
    if (!props.reader) return;
    const { latitude, longitude, opsStatus, suffix, supplyChainStatus, intendedUse, labelIds, responsibleUserId } = values;

    const payload = {
      ...props.reader,
      opsStatus,
      suffix,
      supplyChainStatus,
      intendedUse,
      responsibleUserId,
    } as Reader;

    await updateReader(payload)
      .then(() => notifyMessage('Update Reader success!', 'success'))
      .catch(() => notifyMessage('Unable to update reader', 'error'));

    if (props.reader.lockedLocation?.latitude !== Number(latitude) || props.reader.lockedLocation?.longitude !== Number(longitude)) {
      await updateLockedLocation({
        imei: props.reader?.imei,
        latitude: Number(latitude) ? Number(latitude) : undefined,
        longitude: Number(longitude) ? Number(longitude) : undefined,
      }).catch(() => notifyMessage('Unable to update Lockedlocation', 'error'));
    }

    if (values.reportIntervalSeconds && values.reportIntervalSeconds !== props.reader.reportInterval) {
      await updateReportInterval({
        readerId: props.reader?.id,
        reportIntervalInSeconds: values.reportIntervalSeconds,
      }).catch(() => notifyMessage('Unable to update report interval', 'error'));
    }

    if (labelIds) {
      if (props.reader.labelTypeIds) {
        for (const id of props.reader.labelTypeIds) {
          const existLabel = labelIds.find((lb) => lb === id);
          if (!existLabel) {
            await deleteLabel({ readerId: props.reader?.id, labelId: id }).catch(() => notifyMessage('Unable to delete label', 'error'));
          }
        }
      }
      for (let i = 0; i < labelIds.length; i++) {
        await appendLabel({ readerId: props.reader?.id, labelId: labelIds[i] }).catch(() => notifyMessage('Unable to update label', 'error'));
      }
    }

    await dispatch(fetchReaderByImei(props.reader.imei));
    setIsEditLockedLocation(false);
    dispatch(updateIsEditLockedLocation(false));
    dispatch(updateTargetLocationState(null));
  };

  const debouncedFetchOptions = debounce(async (value) => {
    await fetchUsers(value);
  }, 700);

  const fetchUsers = async (value?: string) => {
    await getUsers(value).then((res: any) => setUsers(res?.data ?? []));
  };

  const formik = useFormik<FormParams>({
    initialValues: {
      opsStatus: props.reader?.opsStatus ?? null,
      suffix: props.reader?.suffix ?? '',
      supplyChainStatus: props.reader?.supplyChainStatus ?? null,
      intendedUse: props.reader?.intendedUse ?? null,
      reportIntervalSeconds: props.reader?.reportInterval ?? null,
      latitude:
        props.reader?.lockedLocation?.latitude !== undefined && props.reader?.lockedLocation?.latitude !== null
          ? props.reader?.lockedLocation?.latitude.toString()
          : null,
      longitude:
        props.reader?.lockedLocation?.longitude !== undefined && props.reader?.lockedLocation?.longitude !== null
          ? props.reader?.lockedLocation?.longitude.toString()
          : null,
      responsibleUserId: props.reader?.responsibleUserId ?? null,
      responsibleUserFullName: props.reader?.responsibleUserFullName ?? null,
    },
    validationSchema: configurationValidationSchema(isEditLockedLocation),
    onSubmit: (values) => handleSubmitForm(values),
  });

  const clearLockedLocation = () => {
    formik.setFieldValue('latitude', null);
    formik.setFieldValue('longitude', null);
    dispatch(updateTargetLocationState(null));
  };

  useEffect(() => {
    dispatch(updateTargetLocationState(null));
    return () => {
      dispatch(updateTargetLocationState(null));
    };
  }, [props.reader?.imei]);

  const saveContracts = async (contractIds: string[]) => {
    updateReaderContracts(props.reader.id, contractIds)
      .then((reader: Reader) => {
        setIsManageContractsDialogOpen(false);
        setContractIds(reader.contractIds ?? []);
      })
      .catch((error) => notifyMessage(error, 'error'));
  };

  const hasLockedLocation = Boolean(formik.values.latitude && formik.values.longitude);

  return (
    <div className='reader-config'>
      <Box sx={{ mx: { xs: 2, md: '5%' } }}>
        <div className='reader-config__card'>
          {/* Top link */}
          <div className='reader-config__toolbar'>
            <button type='button' className='reader-config__link' onClick={() => setIsManageContractsDialogOpen(true)}>
              Manage Contracts
            </button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={1} alignItems='center'>
              {/* Operational Status */}
              <FormRow label='Operational Status'>
                <div style={{ width: '100%' }}>
                  <Select
                    variant='outlined'
                    id='opsStatus'
                    value={formik.values.opsStatus ?? ''}
                    disabled={canEdit}
                    onChange={(e) => formik.setFieldValue('opsStatus', Number(e.target.value))}
                    onBlur={formik.handleBlur}
                    error={formik.touched.opsStatus && Boolean(formik.errors.opsStatus)}
                    size='small'
                    fullWidth
                  >
                    {settingState.readerOpsStatus?.value.map((val, index: number) => (
                      <MenuItem key={index} value={val.text}>
                        {val.value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={formik.touched?.opsStatus && Boolean(formik.errors?.opsStatus)}>
                    {formik.touched?.opsStatus && formik.errors?.opsStatus}
                  </FormHelperText>
                </div>
              </FormRow>

              {/* Supply Chain Status */}
              <FormRow label='Supply Chain Status'>
                <div style={{ width: '100%' }}>
                  <Select
                    variant='outlined'
                    id='supplyChainStatus'
                    value={formik.values.supplyChainStatus ?? ''}
                    disabled={!canEdit}
                    onChange={(e) => formik.setFieldValue('supplyChainStatus', Number(e.target.value))}
                    onBlur={formik.handleBlur}
                    error={formik.touched.supplyChainStatus && Boolean(formik.errors.supplyChainStatus)}
                    size='small'
                    fullWidth
                  >
                    {settingState.readerSupplyChainStatus?.value.map((val, index: number) => (
                      <MenuItem key={index} value={val.text}>
                        {val.value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={formik.touched?.supplyChainStatus && Boolean(formik.errors?.supplyChainStatus)}>
                    {formik.touched?.supplyChainStatus && formik.errors?.supplyChainStatus}
                  </FormHelperText>
                </div>
              </FormRow>

              {/* Suffix */}
              <FormRow label='Reader Name Suffix'>
                <TextField
                  id='suffix'
                  name='suffix'
                  value={formik.values.suffix ?? ''}
                  disabled={!canEdit}
                  onChange={formik.handleChange}
                  error={formik.touched.suffix && Boolean(formik.errors.suffix)}
                  helperText={formik.touched.suffix && formik.errors.suffix}
                  placeholder='Enter Reader Suffix'
                  variant='outlined'
                  size='small'
                  fullWidth
                  autoComplete='off'
                />
              </FormRow>

              {/* Intended Use */}
              <FormRow label='Intended Use'>
                <div style={{ width: '100%' }}>
                  <Select
                    variant='outlined'
                    id='intendedUse'
                    value={formik.values.intendedUse ?? ''}
                    disabled={!canEdit || !canEditReaderIntendedUse}
                    onChange={(e) => formik.setFieldValue('intendedUse', Number(e.target.value))}
                    onBlur={formik.handleBlur}
                    error={formik.touched.intendedUse && Boolean(formik.errors.intendedUse)}
                    size='small'
                    fullWidth
                  >
                    {settingState.readerUsageTypes?.value.map((val, index: number) => (
                      <MenuItem key={index} value={val.text}>
                        {val.value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={formik.touched?.intendedUse && Boolean(formik.errors?.intendedUse)}>
                    {formik.touched?.intendedUse && formik.errors?.intendedUse}
                  </FormHelperText>
                </div>
              </FormRow>

              {/* Report Frequency */}
              <FormRow label='Report Frequency'>
                <div style={{ width: '100%' }}>
                  <Select
                    displayEmpty
                    variant='outlined'
                    id='reportIntervalSeconds'
                    value={formik.values.reportIntervalSeconds ?? ''}
                    disabled={!canEdit || !canEditReaderReportInterval}
                    onChange={(e) => formik.setFieldValue('reportIntervalSeconds', Number(e.target.value))}
                    onBlur={formik.handleBlur}
                    error={formik.touched.reportIntervalSeconds && Boolean(formik.errors.reportIntervalSeconds)}
                    size='small'
                    fullWidth
                    renderValue={(value) => <span>{value ? `${Number(value) / 60} minutes` : 'Select a frequency'}</span>}
                  >
                    {ALLOWED_INTERVAL_SECONDS.map((val, index) => (
                      <MenuItem key={index} value={val}>
                        {val / 60} minutes
                      </MenuItem>
                    ))}
                    {formik.values.reportIntervalSeconds && !ALLOWED_INTERVAL_SECONDS.find((i) => i == formik.values.reportIntervalSeconds) && (
                      <MenuItem value={formik.values.reportIntervalSeconds}>{formik.values.reportIntervalSeconds / 60} minutes</MenuItem>
                    )}
                  </Select>
                  <FormHelperText error={formik.touched?.reportIntervalSeconds && Boolean(formik.errors?.reportIntervalSeconds)}>
                    {formik.touched?.reportIntervalSeconds && formik.errors?.reportIntervalSeconds}
                  </FormHelperText>
                </div>
              </FormRow>

              {/* Labels */}
              <FormRow label='Labels'>
                <LabelTag
                  enableCustomLabel={false}
                  formRef={formik}
                  defaultLabels={props.reader.labelTypeIds}
                  resourceType={ResourceType.Reader}
                  disabled={!canEdit}
                />
              </FormRow>

              {/* Responsible */}
              <FormRow label='Responsible'>
                <Autocomplete
                  disablePortal
                  freeSolo
                  size='small'
                  fullWidth
                  options={users?.map((p: AutoCompleteUser) => ({ id: p.id, label: p.label }))}
                  value={formik.values.responsibleUserFullName ?? ''}
                  disabled={!canEdit}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input' && value) debouncedFetchOptions(value);
                  }}
                  onChange={(_event, value: any | null) => {
                    if (value && users) {
                      const selectedUser = users.find((s) => s.id === value.id);
                      if (selectedUser) {
                        formik.setFieldValue('responsibleUserFullName', selectedUser?.label);
                        formik.setFieldValue('responsibleUserId', selectedUser?.id);
                        return;
                      }
                    }
                    formik.setFieldValue('responsibleUserId', null);
                    formik.setFieldValue('responsibleUserFullName', '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      disabled={!canEdit}
                      id='responsibleUserId'
                      placeholder='Enter Responsible user'
                      variant='outlined'
                      size='small'
                      InputLabelProps={{ shrink: true }}
                      error={formik.touched.responsibleUserId && Boolean(formik.errors.responsibleUserId)}
                      helperText={formik.touched.responsibleUserId && formik.errors.responsibleUserId}
                    />
                  )}
                />
              </FormRow>

              {/* Locked Location */}
              <Grid item xs={12} sm={4} lg={5} className='reader-config__row'>
                <div className='reader-config__label'>Locked Location</div>
              </Grid>
              <Grid item xs={12} sm={7} lg={6} className='reader-config__row'>
                <div className='reader-config__control'>
                  {!isEditLockedLocation ? (
                    <div className='reader-config__latlng-display'>
                      {hasLockedLocation ? `${formik.values.latitude}, ${formik.values.longitude}` : 'None'}
                    </div>
                  ) : (
                    <div className='reader-config__latlng'>
                      <TextField
                        id='latitude'
                        name='latitude'
                        label='Latitude'
                        value={formik.values.latitude ?? ''}
                        onChange={(e) => formik.setFieldValue('latitude', e.target.value)}
                        error={Boolean(formik.errors.latitude)}
                        helperText={formik.touched.latitude && formik.errors.latitude}
                        placeholder='Latitude'
                        variant='outlined'
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        id='longitude'
                        name='longitude'
                        label='Longitude'
                        value={formik.values.longitude ?? ''}
                        onChange={(e) => formik.setFieldValue('longitude', e.target.value)}
                        error={Boolean(formik.errors.longitude)}
                        helperText={formik.touched.longitude && formik.errors.longitude}
                        placeholder='Longitude'
                        variant='outlined'
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item xs={12} sm={1} className='reader-config__row'>
                <AuthGuard permission={AppPermissions.Reader.LockedLocation.Edit}>
                  <div className='reader-config__icon-actions'>
                    {!isEditLockedLocation ? (
                      <>
                        <Tooltip title='Edit' placement='top' disableInteractive>
                          <EditNoteOutlinedIcon
                            onClick={() => {
                              setIsEditLockedLocation(true);
                              dispatch(updateIsEditLockedLocation(true));
                            }}
                          />
                        </Tooltip>
                        {hasLockedLocation && (
                          <Tooltip title='Reset' placement='top' disableInteractive>
                            <ClearIcon
                              data-variant='danger'
                              onClick={() => {
                                clearLockedLocation();
                                dispatch(updateTargetLocationState(null));
                              }}
                            />
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <Tooltip title='Cancel' placement='top' disableInteractive>
                        <CancelIcon
                          data-variant='danger'
                          onClick={() => {
                            setIsEditLockedLocation(false);
                            dispatch(updateIsEditLockedLocation(false));
                            dispatch(updateTargetLocationState(null));
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </AuthGuard>
              </Grid>

              {/* Submit */}
              <AuthGuard permission={AppPermissions.Reader.Edit}>
                <Grid xs={12} item>
                  <div className='reader-config__actions'>
                    <Stack direction='row' spacing={1}>
                      <Button variant='contained' color='primary' type='submit' size='small' disableElevation>
                        Update
                      </Button>
                    </Stack>
                  </div>
                </Grid>
              </AuthGuard>
            </Grid>
          </form>
        </div>
      </Box>
    </div>
  );
};

export default ReaderConfiguration;
